import { http } from './http';
import { ROUTES } from './config';
import type { CreateReviewInput, ModerationReviewsPage, ProductReviewsPage } from '../types/ecommerce';

/**
 * ProductService's review client. Product reviews are public reads (same "anonymous prefix,
 * controller-gated writes" split as productsApi); create is customer-only; moderation is
 * manager-only, enforced in-service via [RequireRoles].
 */
export const reviewsApi = {
  list(productId: string, cursor?: string, pageSize = 10): Promise<ProductReviewsPage> {
    const params = new URLSearchParams({ pageSize: String(pageSize) });
    if (cursor) params.set('cursor', cursor);
    return http.get(`${ROUTES.products}/${productId}/reviews?${params.toString()}`, { anonymous: true });
  },

  /** The owning customer only (401 for anonymous — the gateway requires a real login for the
   * whole /orders-shaped write path here too). */
  create(productId: string, body: CreateReviewInput) {
    return http.post(`${ROUTES.products}/${productId}/reviews`, body);
  },

  /** Whether the signed-in customer has already reviewed this product. */
  hasReviewed(productId: string): Promise<boolean> {
    return http.get(`${ROUTES.products}/${productId}/reviews/mine`);
  },

  /** Manager+ only — every review across every product, for moderation. */
  moderationList(cursor?: string, pageSize = 20): Promise<ModerationReviewsPage> {
    const params = new URLSearchParams({ pageSize: String(pageSize) });
    if (cursor) params.set('cursor', cursor);
    return http.get(`${ROUTES.productReviews}/moderation?${params.toString()}`);
  },

  /** Manager+ only. */
  setHidden(reviewId: string, hidden: boolean): Promise<void> {
    return http.put(`${ROUTES.productReviews}/${reviewId}/hide`, { hidden });
  },

  /** Manager+ only. Publishes (or edits) the public store reply — whether typed from scratch
   * or edited from the AI-suggested draft. */
  publishReply(reviewId: string, reply: string): Promise<void> {
    return http.put(`${ROUTES.productReviews}/${reviewId}/reply`, { reply });
  },
};
